import { type RefObject, useEffect } from 'react';

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Traps keyboard focus within `containerRef` while `active` is true, and
 * restores focus to the previously-focused element when `active` becomes false.
 * Satisfies WCAG 2.1 SC 2.1.2 (No Keyboard Trap) and the ARIA modal contract.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
  /**
   * Where focus should land on open, when it should not be the first focusable
   * element. Pass this rather than scheduling a second `focus()` of your own:
   * two independent moves on the same open transition make focus visibly jump
   * (WCAG 2.4.3 Focus Order). Ignored when the element is missing or cannot
   * take focus — a disabled confirm button would otherwise swallow the move and
   * strand focus on `<body>`, outside the dialog.
   */
  initialFocusRef?: RefObject<HTMLElement | null>,
  /**
   * Where focus goes on close when the element that opened this is no longer in the
   * document.
   *
   * `previouslyFocused.focus()` is a silent no-op on a detached node, so focus stays on
   * `<body>` — and the opener being gone is not exotic here: acting from a dialog is
   * usually what removes the row that opened it. Without a fallback, the most ordinary
   * path through the drawer is the one that strands a keyboard user (WCAG 2.4.3).
   * Callers pass something stable and always present, such as the tab the view lives on.
   */
  restoreFallbackRef?: RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    if (!active || !containerRef.current) return;
    const container = containerRef.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Move focus into the dialog so keyboard/SR users start inside it, even when
    // the caller omits an `autoFocus` element (WCAG 2.4.3 Focus Order). If the
    // caller already set `autoFocus`, the browser has focused that element and it
    // is inside the container, so we leave it; otherwise focus the first element.
    if (!container.contains(document.activeElement)) {
      const preferred = initialFocusRef?.current ?? null;
      const initial =
        preferred && container.contains(preferred) && preferred.matches(FOCUSABLE_SELECTORS)
          ? preferred
          : Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)).filter(
              (el) => !el.closest('[inert]'),
            )[0];
      initial?.focus();
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const focusable = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS),
      ).filter((el) => !el.closest('[inert]'));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;

      if (e.shiftKey) {
        if (document.activeElement === first || !container.contains(document.activeElement)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last || !container.contains(document.activeElement)) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // `isConnected`, not a truthiness check: the reference survives unmounting, so the
      // node is still an object — it just no longer accepts focus.
      if (previouslyFocused?.isConnected === true) previouslyFocused.focus();
      else restoreFallbackRef?.current?.focus();
    };
  }, [active, containerRef, initialFocusRef, restoreFallbackRef]);
}
